// src/pages/ingresos/components/PrintOSButton.jsx
import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "../../../utils/supabase";

// ===== Helpers =====

// Formato fecha DD/MM/AAAA
function formatDateEU(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Convierte OS-5607-001 → 5607001
function certNumberFromCode(code) {
  if (!code) return "";
  return String(code).replace(/\D/g, "");
}

// Cargar imagen (public/) como dataURL para jsPDF
async function loadImageDataUrl(url) {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`No se pudo cargar imagen: ${url}`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function getImageNaturalSize(dataUrl) {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function drawPageFrame(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setLineWidth(0.4);
  doc.rect(5, 5, pageWidth - 10, pageHeight - 10);
}

function drawLabeledRowGrid(doc, x, y, w, rowH, label, value, splitX, fontSize = 8) {
  doc.setLineWidth(0.2);
  doc.rect(x, y, w, rowH);
  doc.line(x + splitX, y, x + splitX, y + rowH);

  doc.setFontSize(fontSize);
  doc.setFont("helvetica", "bold");
  doc.text(`${label}:`, x + 2, y + rowH - 2);

  doc.setFont("helvetica", "normal");
  const text = String(value ?? "");
  const maxW = w - splitX - 4;
  const clipped = text.length > 0 ? doc.splitTextToSize(text, maxW)[0] : "";
  doc.text(clipped, x + splitX + 2, y + rowH - 2);
}

function getTotalPages(doc) {
  if (typeof doc.getNumberOfPages === "function") return doc.getNumberOfPages();
  if (doc.internal && typeof doc.internal.getNumberOfPages === "function") return doc.internal.getNumberOfPages();
  return 1;
}

// ✅ Mini encabezado para páginas 2+ (OS + Cliente + X de Y)
function drawContinuationHeader(doc, pageNum, totalPages, headerNumber, clientHeaderName) {
  const w = doc.internal.pageSize.getWidth();
  const y = 9;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);

  const leftText = `OS-${headerNumber || "-"}`;
  doc.text(leftText, 10, y);

  doc.setFont("helvetica", "normal");
  const centerText = (clientHeaderName || "-").toUpperCase();
  doc.text(centerText, w / 2, y, { align: "center" });

  doc.setFont("helvetica", "bold");
  const rightText = `${pageNum} de ${totalPages}`;
  doc.text(rightText, w - 10, y, { align: "right" });
}

// ✅ Numeración abajo (todas las páginas)
function drawFooterPageNumber(doc, pageNum, totalPages) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);

  doc.text(`${pageNum} de ${totalPages}`, w - 10, h - 7, { align: "right" });
}

// ✅ Tipo para PDF: PRIORIDAD = categoría (instrument_categories.name)
// fallback = variable (instrument_type)
function getTipoForPdf({ categoryName, instrumentType }) {
  const c = String(categoryName || "").trim();
  if (c) return c;
  return String(instrumentType || "").trim();
}

// ===== Component =====

export default function PrintOSButton({ po }) {
  const [loading, setLoading] = useState(false);

  if (!po) return null;

  const handlePrint = async () => {
    try {
      setLoading(true);

      // 1) Cliente ✅ ahora trae locality y province
      let client = null;
      if (po.client_id) {
        const { data, error } = await supabase
          .from("clients")
          .select("id,name,client_number,contact,email,phone,address,locality,province,cuit,tax_id")
          .eq("id", po.client_id)
          .single();
        if (!error) client = data;
      }

      // 2) Instrumentos de la OS (desde la view)
      let instruments = [];
      {
        const selectOld =
          "service_order_id,code,instrument_id,instrument_lab_id,instrument_type,instrument_brand,instrument_model,instrument_serial,instrument_tag,created_at,po_id";

        const { data, error } = await supabase
          .from("v_service_orders_enriched")
          .select(selectOld)
          .eq("po_id", po.id)
          .order("created_at", { ascending: true });

        if (error) throw error;
        instruments = data || [];
      }

      const baseOS = instruments && instruments.length ? instruments[0] : null;

      // 2.b) Observaciones administrativas (desde service_orders)
      let observations = "";
      if (baseOS?.service_order_id) {
        const { data: obsRow } = await supabase
          .from("service_orders")
          .select("general_observations")
          .eq("id", baseOS.service_order_id)
          .single();

        observations = obsRow?.general_observations || "";
      }

      // 2.c) Puente service_orders -> instruments (para obs + categoría)
      const soIds = (instruments || []).map((r) => r.service_order_id).filter(Boolean);

      const soIdToInstrumentId = new Map();
      const instrumentObsById = new Map();
      const instrumentCategoryById = new Map();

      if (soIds.length > 0) {
        const { data: soRows, error: soErr } = await supabase
          .from("service_orders")
          .select("id,instrument_id")
          .in("id", soIds);

        if (soErr) throw soErr;

        const instrumentIds = [];
        (soRows || []).forEach((row) => {
          if (row?.id) soIdToInstrumentId.set(row.id, row.instrument_id || null);
          if (row?.instrument_id) instrumentIds.push(row.instrument_id);
        });

        const uniqueInstrumentIds = Array.from(new Set(instrumentIds)).filter(Boolean);

        if (uniqueInstrumentIds.length > 0) {
          const { data: instRows, error: instErr } = await supabase
            .from("instruments")
            .select("id,observations,category:instrument_categories ( id, name )")
            .in("id", uniqueInstrumentIds);

          if (instErr) throw instErr;

          (instRows || []).forEach((row) => {
            instrumentObsById.set(row.id, row.observations || "");
            instrumentCategoryById.set(row.id, row.category?.name || "");
          });
        }
      }

      // NÚMERO CABECERA
      const firstCode = baseOS?.code || null;
      const firstCertDigits = firstCode ? certNumberFromCode(firstCode) : null;

      const fallbackFromPoCode = (po.code || "").replace(/^OC-/i, "").replace(/^OS-/i, "");
      const fallbackFromBase = po.base_number != null ? String(po.base_number) : "";

      const headerNumber = firstCertDigits || fallbackFromPoCode || fallbackFromBase || "";

      const clientHeaderName = (client?.name || po.client_name || "-")
        .toUpperCase()
        .slice(0, 40);

      // PDF A4 apaisado
      const doc = new jsPDF("l", "mm", "a4");

      // Marco externo (primera página)
      drawPageFrame(doc);

      const pageWidth = doc.internal.pageSize.getWidth();

      // Logo
      try {
        const logoUrl = "/logo-calins-color.png";
        const logoDataUrl = await loadImageDataUrl(logoUrl);
        const { w, h } = await getImageNaturalSize(logoDataUrl);

        const targetW = 42;
        const maxH = 16;
        const ratio = h / w;

        let logoWmm = targetW;
        let logoHmm = targetW * ratio;

        if (logoHmm > maxH) {
          logoHmm = maxH;
          logoWmm = maxH / ratio;
        }

        doc.addImage(logoDataUrl, "PNG", 10, 10, logoWmm, logoHmm);
      } catch (e) {
        console.warn("[PrintOSButton] No se pudo cargar el logo:", e);
      }

      // Título (solo en página 1)
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("ORDEN DE SERVICIO", pageWidth / 2, 15, { align: "center" });

      // Caja OS (arriba derecha)
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.rect(pageWidth - 70, 10, 60, 20);
      doc.text("OS-", pageWidth - 64, 18);

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(headerNumber, pageWidth - 40, 20, { align: "center" });

      doc.setFontSize(10);
      doc.text((client?.name || po.client_name || "-").toUpperCase().slice(0, 26), pageWidth - 40, 28, {
        align: "center",
      });

      // Centro de Costos
      const cc = po.base_number != null ? String(po.base_number) : "";
      doc.setLineWidth(0.3);
      doc.rect(pageWidth / 2 - 30, 20, 60, 12);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Centro de Costos", pageWidth / 2, 25, { align: "center" });
      doc.setFontSize(12);
      doc.text(cc || "-", pageWidth / 2, 30, { align: "center" });

      // BLOQUE ENCABEZADO
      const headerX = 10;
      const headerTop = 34;

      // ✅ Un poco más alto para agregar Localidad y Provincia en filas separadas
      const headerH = 42;

      const headerW = pageWidth - 20;

      const clientColW = 100;
      const checkColW = 60;
      const rightColW = headerW - clientColW - checkColW;

      const xClient = headerX;
      const xCheck = headerX + clientColW;
      const xRight = xCheck + checkColW;

      doc.setLineWidth(0.4);
      doc.rect(headerX, headerTop, headerW, headerH);

      doc.setLineWidth(0.3);
      doc.line(xCheck, headerTop, xCheck, headerTop + headerH);
      doc.line(xRight, headerTop, xRight, headerTop + headerH);

      const fecha = formatDateEU(po.created_at || new Date());

      // ✅ Ahora va por filas: Dirección / Localidad / Provincia
      const rows = [
        ["Fecha", fecha],
        ["Cliente", client?.name || po.client_name || "-"],
        ["Cliente Nº", client?.client_number ?? "-"],
        ["Dirección", client?.address ?? "-"],
        ["Localidad", client?.locality ?? "-"],
        ["Provincia", client?.province ?? "-"],
        ["Contacto", client?.contact ?? "-"],
        ["Teléfono", client?.phone ?? "-"],
        ["CUIT", client?.cuit || client?.tax_id || "-"],
      ];

      const rowH = headerH / rows.length;
      const splitX = 22;
      rows.forEach((r, idx) => {
        const yy = headerTop + idx * rowH;
        drawLabeledRowGrid(doc, xClient, yy, clientColW, rowH, r[0], r[1], splitX, 8);
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("CHECK LIST PROCESO", xCheck + checkColW / 2, headerTop + 6, { align: "center" });

      doc.setLineWidth(0.2);
      doc.line(xCheck, headerTop + 8, xRight, headerTop + 8);

      const checkItems = ["CALIBRACIÓN", "CERTIFICACIÓN", "REVISIÓN FINAL", "ENTREGA"];
      doc.setFontSize(9);
      checkItems.forEach((label, idx) => {
        const yy = headerTop + 13 + idx * 6;
        doc.rect(xCheck + 6, yy - 4, 4, 4);
        doc.setFont("helvetica", "bold");
        doc.text(label, xCheck + 13, yy - 1);
      });

      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("Tipo de Servicio:", xRight + 4, headerTop + 6);
      doc.text("CALIBRACION", xRight + 38, headerTop + 6);

      doc.setLineWidth(0.2);
      doc.line(xRight, headerTop + 8, headerX + headerW, headerTop + 8);

      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("Observaciones:", xRight + 4, headerTop + 13);

      const obsBoxX = xRight + 4;
      const obsBoxY = headerTop + 15;
      const obsBoxW = rightColW - 8;
      const obsBoxH = headerH - 18;

      doc.setLineWidth(0.3);
      doc.rect(obsBoxX, obsBoxY, obsBoxW, obsBoxH);

      if (observations) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        const wrapped = doc.splitTextToSize(observations, obsBoxW - 4);
        doc.text(wrapped, obsBoxX + 2, obsBoxY + 4);
      }

      // TABLA
      const tableStartY = headerTop + headerH + 6;

      const head = [
        ["Cert Nº", "ID Inst.", "Tipo", "Marca", "Modelo", "Nº Serie", "TAG", "Obs.", "F. Cal.", "Cert Realiz."],
      ];

      const body = (instruments || []).map((r) => {
        const certNumRaw = certNumberFromCode(r.code);
        const certNum = certNumRaw ? certNumRaw.padStart(7, "0") : "";

        const instId = soIdToInstrumentId.get(r.service_order_id) || null;
        const instLabId = r.instrument_lab_id || "";
        const instrumentIdPdf = instLabId || instId || "";
        const instObs = instId ? instrumentObsById.get(instId) || "" : "";

        const categoryName = instId ? instrumentCategoryById.get(instId) || "" : "";
        const tipoPdf = getTipoForPdf({
          categoryName,
          instrumentType: r.instrument_type,
        });

        return [
          certNum,
          instrumentIdPdf,
          tipoPdf,
          r.instrument_brand || "",
          r.instrument_model || "",
          r.instrument_serial || "",
          r.instrument_tag || "",
          instObs,
          "",
          "",
        ];
      });

      // ✅ Reserva para pie
      const FOOTER_H = 26;
      const FOOTER_GAP = 6;

      autoTable(doc, {
        head,
        body,
        startY: tableStartY,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 1.2,
          overflow: "linebreak",
          valign: "top",
          lineWidth: 0.15,
          lineColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: 20,
          lineWidth: 0.2,
          lineColor: [0, 0, 0],
          fontStyle: "bold",
        },
        margin: {
          left: 10,
          right: 10,
          bottom: FOOTER_H + FOOTER_GAP + 10,
        },
        columnStyles: {
          0: { cellWidth: 18 },
          1: { cellWidth: 18 },
          2: { cellWidth: 20 },
          3: { cellWidth: 24 },
          4: { cellWidth: 26 },
          5: { cellWidth: 24 },
          6: { cellWidth: 24 },
          7: { cellWidth: 86 },
          8: { cellWidth: 16 },
          9: { cellWidth: 20 },
        },
        didDrawPage: () => {
          drawPageFrame(doc);
        },
      });

      // PIE (última página)
      const totalPages = getTotalPages(doc);
      if (typeof doc.setPage === "function") doc.setPage(totalPages);

      const pW = doc.internal.pageSize.getWidth();
      const pH = doc.internal.pageSize.getHeight();

      let fy = pH - 26;

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");

      const respAreaW = 150;
      const respAreaX = (pW - respAreaW) / 2;
      const respLabel = "RESPONSABLE DE CALIBRACIÓN:";
      doc.setFont("helvetica", "bold");
      doc.text(respLabel, respAreaX, fy);
      doc.setFont("helvetica", "normal");
      const respLabelW = doc.getTextWidth(respLabel);
      doc.setLineWidth(0.3);
      doc.line(respAreaX + respLabelW + 2, fy + 0.5, respAreaX + respAreaW, fy + 0.5);

      const rightAreaW = 120;
      const rightAreaX = pW - 10 - rightAreaW;

      const drawRightLine = (label, yy) => {
        doc.setFont("helvetica", "bold");
        doc.text(label, rightAreaX, yy);
        const lw = doc.getTextWidth(label);
        doc.setFont("helvetica", "normal");
        doc.setLineWidth(0.3);
        doc.line(rightAreaX + lw + 2, yy + 0.5, rightAreaX + rightAreaW, yy + 0.5);
      };

      drawRightLine("FECHA RECEPCIÓN EN LABORATORIO:", fy);
      fy += 7;
      drawRightLine("PLAZO DE ENTREGA:", fy);
      fy += 7;

      doc.setFont("helvetica", "bold");
      doc.text("FIRMA:", rightAreaX, fy);
      const fw = doc.getTextWidth("FIRMA:");
      doc.setFont("helvetica", "normal");
      doc.setLineWidth(0.3);
      doc.line(rightAreaX + fw + 2, fy + 0.5, rightAreaX + rightAreaW - 20, fy + 0.5);

      // ✅ Numeración + Mini-cabecera en páginas 2+
      for (let i = 1; i <= totalPages; i++) {
        if (typeof doc.setPage === "function") doc.setPage(i);

        drawFooterPageNumber(doc, i, totalPages);

        if (i >= 2) {
          drawContinuationHeader(doc, i, totalPages, headerNumber, clientHeaderName);
        }
      }

      // Guardar
      const fileName = `OS-${headerNumber || "sin-numero"}.pdf`;
      doc.save(fileName);
    } catch (e) {
      console.error("[PrintOSButton] error:", e);
      alert(e.message || "No se pudo generar el PDF de la OS.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      className="px-2 py-1.5 rounded-md border border-slate-500/40 text-slate-200 hover:bg-slate-500/10 disabled:opacity-60"
      onClick={handlePrint}
      disabled={loading}
      title="Imprimir Orden de Servicio en PDF"
    >
      {loading ? "Generando…" : "🖨️ OS PDF"}
    </button>
  );
}
